import { CheckIcon, ChevronDownIcon, TaskIcon, getIconComponent } from "@storytell/bsky-icons";
import { ChatMessages, type Icons } from "@storytell/cp-client";
import {
  type StreamingChatId,
  StreamingChatStatus,
  type StreamingOperation,
  isEndTurnOperationId,
} from "@storytell/streaming";
import { type Component, Match, Switch } from "solid-js";
import { Dynamic } from "solid-js/web";
import { ProcessingSpinner } from "~/components/ui";
import { useWire } from "~/wire";

interface OperationHeaderProps {
  operation: StreamingOperation;
  title: string;
  isExpanded: boolean;
  isProcessing: boolean;
  hasBlocks: boolean;
  onToggle: () => void;
  chatId: StreamingChatId;
}

export const OperationHeader: Component<OperationHeaderProps> = (props) => {
  const wire = useWire();
  // Map protobuf icon enum to our icon components
  const mapIconToComponent = (iconEnum?: Icons.Icon | string, size = 20, className = "stroke-2 w-3 h-3") => {
    return <Dynamic component={getIconComponent(iconEnum) || TaskIcon} size={size} class={className} />;
  };

  const isOperationRunning = (operation: StreamingOperation) => {
    return (
      operation.status === ChatMessages.OperationStatus.RUNNING
      // TODO: @andi streaming
      // || operation.taskStatus === ChatMessages.TaskStatus.RUNNING
    );
  };

  const getCompleteIcon = (operation: StreamingOperation) => {
    if (isEndTurnOperationId(operation.operationId)) {
      return <CheckIcon size={16} class="stroke-3 w-3 h-3" />;
    }
    return <CheckIcon size={16} class="stroke-3 w-3 h-3" />;
  };

  // TODO: @andi streaming
  const getOperationIcon = (operation: StreamingOperation) => {
    const isEndTurn = isEndTurnOperationId(operation.operationId);
    const operationCompleted = operation.isComplete;
    // const allTasksCompleted = operation.taskStatus === ChatMessages.TaskStatus.COMPLETED;
    // const isProcessing =
    //   !operationCompleted &&
    //   (operation.taskStatus === ChatMessages.TaskStatus.PLANNED ||
    //     operation.taskStatus === ChatMessages.TaskStatus.RUNNING ||
    //     operation.blocks.some((block) => !block.isComplete));

    // // Operation is truly complete if:
    // // 1. It's not processing AND
    // // 2. Either the operation status is COMPLETED OR task is completed
    // const isTrulyComplete = !isProcessing && (operationCompleted || allTasksCompleted);

    const iconElement = operationCompleted
      ? getCompleteIcon(operation)
      : // Try to get icon from the operation or first block
        (() => {
          if (isEndTurn || isOperationRunning(props.operation)) {
            return <ProcessingSpinner class="border-[1.5px] border-primary-text border-t-transparent" />;
          }
          // if (operation.icon) {
          //   return mapIconToComponent(operation.icon, 16);
          // }
          // const firstBlockWithIcon = operation.blocks.find((block) => block.icon);
          // if (firstBlockWithIcon?.icon) {
          //   return mapIconToComponent(firstBlockWithIcon.icon, 16);
          // }
          // For work to be done (initial state or processing) - fallback
          return <TaskIcon size={16} class="stroke-2" />;
        })();

    // Wrap icon in a circle
    return (
      <div class="w-5 h-5 rounded-full relative top-[6px] left-[7px] bg-dark-icon text-primary-text flex items-center justify-center">
        {iconElement}
      </div>
    );
  };
  return (
    <div
      data-renderer="operation-header"
      class="flex items-start justify-between mb-4"
      data-operation={props.operation.operationId}
    >
      {/* Main content: icon, title, expand/collapse */}
      <div class="flex items-start flex-1 gap-4 min-w-0">
        {/* TODO: @andi streaming */}
        <div class="flex-shrink-0 -mt-0.5">{getOperationIcon(props.operation)}</div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 group">
            <Switch>
              <Match
                when={
                  !isEndTurnOperationId(props.operation.operationId) ||
                  (isEndTurnOperationId(props.operation.operationId) &&
                    wire.stream.chat.getStatus(props.chatId) === StreamingChatStatus.IDLE)
                }
              >
                <button
                  type="button"
                  onClick={props.onToggle}
                  class="text-base pt-0.5 font-medium text-primary-text transition-colors text-left"
                >
                  {props.title}
                </button>
                <button
                  type="button"
                  onClick={props.onToggle}
                  class="flex-shrink-0 p-1 pt-2 hover:bg-canvas-hover rounded transition-colors"
                  title={props.isExpanded ? "Collapse operation" : "Expand operation"}
                >
                  <ChevronDownIcon
                    size={12}
                    class="stroke-3 text-muted-text transition-transform duration-100"
                    classList={{
                      "-rotate-90": !props.isExpanded,
                    }}
                  />
                </button>
              </Match>
              <Match when={isEndTurnOperationId(props.operation.operationId)}>
                <div class="text-base pt-1 font-medium text-primary-text hover:text-secondary-text transition-colors text-left">
                  {props.operation.status === ChatMessages.OperationStatus.COMPLETED ? "Done" : "Processing..."}
                </div>
              </Match>
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
};
